/**
 * Utility functions for sending Slack notifications
 */

/**
 * Send a Slack notification for a new user signup
 * @param {Object} userInfo - User information
 * @param {string} userInfo.email - User's email address
 * @param {string} userInfo.userId - User's ID
 * @param {string} userInfo.signupMethod - How they signed up (email, google, etc.)
 * @param {string} userInfo.timestamp - When they signed up
 */
export async function sendSignupNotification(userInfo) {
  try {
    console.log("🎉 [Slack] Sending signup notification for:", userInfo.email);

    const message = {
      text: "🎉 New User Signup!",
      blocks: [
        {
          type: "header",
          text: {
            type: "plain_text",
            text: "🎉 New User Signup!",
            emoji: true,
          },
        },
        {
          type: "section",
          fields: [
            {
              type: "mrkdwn",
              text: `*Email:* ${userInfo.email}`,
            },
            {
              type: "mrkdwn",
              text: `*Signup Method:* ${userInfo.signupMethod}`,
            },
            {
              type: "mrkdwn",
              text: `*User ID:* ${userInfo.userId}`,
            },
            {
              type: "mrkdwn",
              text: `*Time:* ${userInfo.timestamp}`,
            },
          ],
        },
      ],
    };

    const response = await fetch("/api/signup-notification", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(message),
    });

    if (!response.ok) {
      const responseData = await response.json();
      console.error("❌ [Slack] Signup notification failed:", {
        status: response.status,
        statusText: response.statusText,
        responseData,
      });
      return false;
    }

    const responseData = await response.json();
    console.log(
      "✅ [Slack] Signup notification sent successfully:",
      responseData
    );
    return true;
  } catch (error) {
    console.error("❌ [Slack] Failed to send signup notification:", error);
    return false;
  }
}

/**
 * Send a Slack notification for a successful checkout when publishing a space
 * @param {Object} checkoutInfo - Checkout information
 * @param {string} checkoutInfo.email - User's email address
 * @param {string} checkoutInfo.userId - User's ID
 * @param {string} checkoutInfo.spaceTitle - Space title
 * @param {string} checkoutInfo.spaceSlug - Space slug
 * @param {string} checkoutInfo.spaceUrl - Full space URL
 * @param {string} checkoutInfo.paymentType - Type of payment (trial, immediate, etc.)
 * @param {number} checkoutInfo.amount - Payment amount in dollars
 * @param {string} checkoutInfo.timestamp - When the checkout happened
 */
export async function sendCheckoutNotification(checkoutInfo) {
  // DISABLED: Only signup notifications are enabled
  console.log("ℹ️ Skipping Slack notification for checkout (notifications disabled except signups)");
  return false;
  try {
    console.log(
      "💰 [Slack] Sending checkout notification for:",
      checkoutInfo.email
    );

    const message = {
      text: "💰 New Space Published with Payment!",
      blocks: [
        {
          type: "header",
          text: {
            type: "plain_text",
            text: "💰 Space Published with Payment!",
            emoji: true,
          },
        },
        {
          type: "section",
          fields: [
            {
              type: "mrkdwn",
              text: `*User:* ${checkoutInfo.email}`,
            },
            {
              type: "mrkdwn",
              text: `*Payment Type:* ${checkoutInfo.paymentType}`,
            },
            {
              type: "mrkdwn",
              text: `*Amount:* $${checkoutInfo.amount.toFixed(2)}`,
            },
            {
              type: "mrkdwn",
              text: `*Time:* ${checkoutInfo.timestamp}`,
            },
          ],
        },
        {
          type: "section",
          fields: [
            {
              type: "mrkdwn",
              text: `*Space Title:* ${checkoutInfo.spaceTitle}`,
            },
            {
              type: "mrkdwn",
              text: `*Space URL:* <${checkoutInfo.spaceUrl}|${checkoutInfo.spaceSlug}>`,
            },
          ],
        },
      ],
    };

    const response = await fetch("/api/subscription-notification", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(message),
    });

    if (!response.ok) {
      const responseData = await response.json();
      console.error("❌ [Slack] Checkout notification failed:", {
        status: response.status,
        statusText: response.statusText,
        responseData,
      });
      return false;
    }

    const responseData = await response.json();
    console.log(
      "✅ [Slack] Checkout notification sent successfully:",
      responseData
    );
    return true;
  } catch (error) {
    console.error("❌ [Slack] Failed to send checkout notification:", error);
    return false;
  }
}
